import {Injectable} from "@angular/core";
import {ValidationMsg} from "../../../umbrella-common/services/form.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Country, Order} from "../../../store/state/order.state";
import {UmbrellaValidators} from "../../../umbrella-common/validation/umbrella-validators";
import {AuthenticationService} from "../../../authentication/authentication.service";
import {distinctUntilChanged, takeWhile} from "rxjs/operators";
import {AppState} from "../../../store/state/app.state";
import {Store} from "@ngrx/store";
import {OrderActions} from "../../../store/actions/order.actions";
import {BaseOrderFormService} from "../common/base-order-form.service";
import {AccountExistsValidatorFactory} from "../../../umbrella-common/validation/account-exists-validator.service";
import {TranslateService} from "@ngx-translate/core";
import {StandaloneFormService} from "../../../umbrella-common/services/standalone-form.service";

@Injectable()
export class ContactPersonFormService extends StandaloneFormService implements BaseOrderFormService {

	private FORM_CONTROL_ERROR_MSG_MAP: { [validationKey: string]: ValidationMsg[] } = {
		pattern: [{formControlName: 'phone', msg: 'validations.pattern-phone'}],
		too_long_phone: [{formControlName: 'phone', msg: 'validations.too-many-characters'}],
		requiredLength: [{msg: 'validations.zip-length'}],
		accountexists: [{msg: 'validations.accountexists'}]
	};

	constructor(private fb: FormBuilder, private authService: AuthenticationService, private store: Store<AppState>, private accountExistsValidatorFactory: AccountExistsValidatorFactory, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [order, includeAccountExistsValidator, emailToExcludeFromAccountValidation, countries]: [Order, boolean, string | null, Country[]]) {
		let countryCode = countries.find(c => c.code == (!!order.clientCountry ? order.clientCountry : this.translate.currentLang.split('-')[1])).callPrefix;
		this.store.dispatch(OrderActions.SET_COUNTRY_CODE({countryCode}));

		const accountExistsValidator = this.accountExistsValidatorFactory.create(emailToExcludeFromAccountValidation);
		const form = this.fb.group({});
		form.addControl(`firstName`, this.fb.control(order.contactPerson.firstName, [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]));
		form.addControl(`lastName`, this.fb.control(order.contactPerson.lastName, [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]));
		form.addControl(`jobTitle`, this.fb.control(order.contactPerson.jobTitle, [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]));
		form.addControl(`countryCode`, this.fb.control(countryCode, [Validators.required]));
		form.addControl(`phone`, this.fb.control(this.getPhone(order), [Validators.required, UmbrellaValidators.phoneNumber]));
		form.addControl(`email`, this.fb.control(this.getEmail(order), [Validators.required, UmbrellaValidators.email()], includeAccountExistsValidator ? [accountExistsValidator] : null));
		form.addControl(`salesperson`, this.fb.control(order.contactPerson.salesperson));
		form.controls["countryCode"].disable();

		this.initFormControls(form);
		return form;
	}

	getControlNamesInOrder(): string[] {
		return ['firstName', 'lastName', 'jobTitle', 'email', 'phone', 'salesperson', 'countryCode'];
	}

	fillOrder(order: Order): Order {
		order.contactPerson.firstName = this.getValueFor('firstName', StandaloneFormService.FORM_KEY);
		order.contactPerson.lastName = this.getValueFor('lastName', StandaloneFormService.FORM_KEY);
		order.contactPerson.jobTitle = this.getValueFor('jobTitle', StandaloneFormService.FORM_KEY);
		order.contactPerson.countryCode = this.getValueFor('countryCode', StandaloneFormService.FORM_KEY);
		order.contactPerson.phoneNumber = (this.getValueFor('phone', StandaloneFormService.FORM_KEY) as number).toString();
		order.contactPerson.email = this.getValueFor('email', StandaloneFormService.FORM_KEY);
		order.contactPerson.salesperson = this.getValueFor('salesperson', StandaloneFormService.FORM_KEY);

		return order;
	}

	protected override getFormSpecificErrorMap(): { [p: string]: ValidationMsg[] } {
		return this.FORM_CONTROL_ERROR_MSG_MAP;
	}

	private getCountryCode(): string {
		return this.getValueFor('countryCode', StandaloneFormService.FORM_KEY).split(': ')[1];
	}

	private getEmail(order: Order): string {
		if (this.authService.isLoggedAnonymousClient()) {
			return order.contactPerson.email;
		} else if (order.contactPerson.email != null && this.authService.isDekraEmployeeLogged()) {
			return order.contactPerson.email;
		} else if (order.contactPerson.email == null && this.authService.isLoggedUserClientService()) {
			return null;
		} else if (this.authService.isLoggedIn()) {
			return this.authService.getUser().email;
		}

		return order.contactPerson.email != null ? order.contactPerson.email : null;
	}

	private initFormControls(form: FormGroup) {
		form.controls['firstName'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(firstName => this.store.dispatch(OrderActions.SET_CONTACT_PERSON_FIRST_NAME({firstName})));

		form.controls['lastName'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(lastName => this.store.dispatch(OrderActions.SET_CONTACT_PERSON_LAST_NAME({lastName})));

		form.controls['jobTitle'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(jobTitle => this.store.dispatch(OrderActions.SET_CONTACT_PERSON_JOB_TITLE({jobTitle})));

		form.controls['countryCode'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(countryCode => this.store.dispatch(OrderActions.SET_COUNTRY_CODE({countryCode})));

		form.controls['phone'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(phoneNumber => this.store.dispatch(OrderActions.SET_PHONE_NUMBER({phoneNumber: phoneNumber == null ? null : (phoneNumber as number).toString()})));

		form.controls['email'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(email => this.store.dispatch(OrderActions.SET_CLIENT_EMAIL({email: this.toLowerCase(email)})));

		form.controls['salesperson'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(salesperson => this.store.dispatch(OrderActions.SET_SALESPERSON({salesperson})));
	}

	private toLowerCase(value: string): string {
		return value != null ? value.toLowerCase() : value;
	}

	private getPhone(order: Order): string {
		const phone: string = order.contactPerson.phoneNumber;
		return phone != null ? phone.replace('+1', '') : null;
	}
}
